import React from 'react'
import axios from 'axios'
import {
    addDays,
    addWeeks,
    endOfWeek,
    format,
    isSameDay,
    startOfWeek,
    subWeeks,
} from 'date-fns'
import {
    MdAdd,
    MdChevronLeft,
    MdChevronRight,
    MdContentCopy,
    MdDirectionsCar,
    MdHome,
} from 'react-icons/md'
import { baseUrl } from '../../../assets/Animations'
import { getRequestHeaders } from '../../../api'
import FamilySearchPopup from '../../../Calendar/FamilySearchPopup/FamilySearchPopup'
import NewCalendarEvent from '../../../Calendar/NewCalendarEvent/NewCalendarEvent'
import UnsavedCalendarEventPopup from '../../../Calendar/UnsavedCalendarEventPopup/UnsavedCalendarEventPopup'
import { getTeacherProfileUrl } from '../../FamilyInfoGutter/ContactInfoTab/contactInfoCopyUtils'
import {
    changeDateTimeFromUTCStringToSchoolTimeZone,
    convertStringToUTCString,
    createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime,
    findDuplicateCalendarEvent,
    generateNewCalendarEvent,
    getSchoolIANATimezone,
} from '../../../Calendar/calendarFunctions'
import {
    formatMapAddress,
    getSuggestionObject,
    getSuggestionTeacherKey,
    getTeacherAvailabilityForSuggestion,
    getUniqueSuggestedTeachers,
} from '../magicSchedulerUtils'
import {
    CALENDAR_END_MINUTES,
    CALENDAR_START_MINUTES,
    calendarEventMatchesSuggestion,
    formatMinutes,
    getCalendarEventDriveTime,
    getCalendarMinutesFromPointer,
    getCalendarPosition,
    getEveryOtherWeekGhostEventsForDate,
    getFamilyAvailabilityBlocksForDate,
    getRecurringExceptionConflictForEvent,
    getRecurringExceptionGhostEventsForDate,
    getTeacherAvailabilityBlocksForDate,
} from './magicSchedulerCalendarUtils'
import './magic-scheduler-calendar.css'

const HOURS = Array.from(
    { length: ((CALENDAR_END_MINUTES - CALENDAR_START_MINUTES) / 60) + 1 },
    (_, index) => CALENDAR_START_MINUTES + (index * 60)
)
const EMPTY_FAMILY_PROFILE = {}
const INTRO_EVENT_BACKGROUND_COLOR = '#FEF3C7'
const INTRO_EVENT_BORDER_COLOR = '#F59E0B'
const INTRO_EVENT_TEXT_COLOR = '#92400E'

const normalizeId = (value) => String(value || '').trim()

const formatAvailabilityTimeRange = (startMinutes, endMinutes) => (
    `${formatMinutes(startMinutes).replace(':00', '')} – ${formatMinutes(endMinutes).replace(':00', '')}`
)

const normalizeFamilyAddressRecord = (profile = {}, fallbackAddress = '') => {
    const address = profile?.address || profile?.familyAddress || profile?.Address
    if (!address || typeof address !== 'object') {
        return fallbackAddress ? { addressLine1: String(fallbackAddress).trim() } : {}
    }
    return {
        ...address,
        addressLine1: address.addressLine1
            || address.AddressLine1
            || address.StreetAddress
            || address.streetAddress
            || '',
        addressLine2: address.addressLine2 || address.AddressLine2 || '',
        city: address.city || address.City || '',
        state: address.state || address.State || '',
        zipcode: address.zipcode
            || address.zipCode
            || address.Zipcode
            || address.Zip
            || '',
    }
}

const getStudentId = (student = {}) => normalizeId(
    student?.studentId || student?.StudentID || student?.id || student?._id
)

const getStudentName = (student = {}) => String(
    student?.studentName || student?.StudentName || student?.name || ''
).trim()

const resolveTeacherImage = (teacher, suggestionObject) => {
    const image = String(
        teacher?.ImageName
        || teacher?.imageUrl
        || suggestionObject?.imageUrl
        || ''
    ).trim()
    if (!image) return ''
    return image.startsWith('http') ? image : `https://app.atharo.app/images/${image}`
}

const getInstrumentLabels = (teacher, suggestionObject) => {
    const list = teacher?.InstrumentList
        || suggestionObject?.InstrumentList
        || suggestionObject?.instrumentList
        || []
    return (Array.isArray(list) ? list : [])
        .map((instrument) => (
            typeof instrument === 'string'
                ? instrument
                : instrument?.parentInstrument || instrument?.name
        ))
        .map((instrument) => String(instrument || '').trim())
        .filter(Boolean)
        .filter((instrument, index, all) => all.indexOf(instrument) === index)
}

const getTeacherRecordId = (teacher) => normalizeId(
    teacher?._id || teacher?.MongoId || teacher?.id
)

const getTeacherEmail = (teacher) => (
    teacher?.CalendarEmail
    || teacher?.calendarEmail
    || teacher?.calendarConnectionEmail
    || teacher?.PersonalEmail
    || teacher?.personalEmail
    || teacher?.Email
    || teacher?.email
    || null
)

const hashRouteValue = (value) => {
    let hash = 5381
    const text = String(value || '').trim().toLowerCase()
    for (let index = 0; index < text.length; index += 1) {
        hash = ((hash << 5) + hash) ^ text.charCodeAt(index)
    }
    return (hash >>> 0).toString(36)
}

const getRouteEndpointKey = (familyId, fallbackValue) => (
    familyId
        ? `family:${String(familyId).trim()}`
        : `scheduler-location:${hashRouteValue(fallbackValue)}`
)

const getEventRouteIdentity = (event) => String(
    event?.familyId
    || event?.familyID
    || event?.FamilyID
    || event?.eventId
    || event?.calendarEventId
    || event?._id
    || event?.id
    || formatMapAddress(event?.familyAddress || event?.address)
    || ''
).trim()

const getEventDriveBucket = (event) => {
    const date = new Date(event?.dateTime)
    const hour = Number.isNaN(date.getTime()) ? 17 : date.getHours()
    return `${String(hour).padStart(2, '0')}:00`
}

const getEventDriveTimeRouteKey = (event, schoolId, familyId, familyAddress) => {
    const routeIdentity = getEventRouteIdentity(event)
    if (!routeIdentity) return ''
    return [
        schoolId,
        routeIdentity,
        familyId || hashRouteValue(familyAddress),
        getEventDriveBucket(event),
    ].join(':')
}

const prepareSchedulerRecordForSchoolTime = (
    record,
    fallbackSchool,
    convertExceptions = false
) => {
    const recordWithSchool = {
        ...record,
        school: record?.school || fallbackSchool,
        recurringEndDate: record?.recurringEndDate || '',
    }
    const schoolTimeRecord = changeDateTimeFromUTCStringToSchoolTimeZone(
        recordWithSchool
    )
    if (!convertExceptions) return schoolTimeRecord

    return {
        ...schoolTimeRecord,
        recurringExceptions: (Array.isArray(record?.recurringExceptions)
            ? record.recurringExceptions
            : []).map((exception) => (
            changeDateTimeFromUTCStringToSchoolTimeZone({
                ...recordWithSchool,
                dateTime: exception,
                recurringEndDate: '',
            }).dateTime
        )),
    }
}

const MagicSchedulerCalendar = ({
    suggestions = [],
    teachers = [],
    teacherColorMap = {},
    familyAvailability = {},
    familyAddress = '',
    familyId = '',
    familyName = 'Family',
    familyProfile = EMPTY_FAMILY_PROFILE,
    schoolId = '',
    socket,
    userData = { Name: 'Admin' },
    availabilityMatchOn,
    onToggleAvailabilityMatch,
    preferredTeacherKey = '',
    highlightedSuggestion = null,
    showTeacherList = true,
}) => {
    const todayWeekStart = React.useMemo(
        () => startOfWeek(new Date(), { weekStartsOn: 0 }),
        []
    )
    const nextWeekStart = React.useMemo(
        () => startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 0 }),
        []
    )
    const [weekAnchor, setWeekAnchor] = React.useState(todayWeekStart)
    const calendarTeachers = React.useMemo(
        () => getUniqueSuggestedTeachers(suggestions),
        [suggestions]
    )
    const [selectedTeacherKey, setSelectedTeacherKey] = React.useState('')
    const [calendarEvents, setCalendarEvents] = React.useState([])
    const [calendarCancellations, setCalendarCancellations] = React.useState([])
    const [calendarLoading, setCalendarLoading] = React.useState(false)
    const [calendarError, setCalendarError] = React.useState('')
    const [cancellationLoadWarning, setCancellationLoadWarning] = React.useState('')
    const [syncStatus, setSyncStatus] = React.useState('idle')
    const [hoveredEvent, setHoveredEvent] = React.useState(null)
    const [resolvedDriveTimes, setResolvedDriveTimes] = React.useState({})
    const [openEvent, setOpenEvent] = React.useState(false)
    const [newEvent, setNewEvent] = React.useState(false)
    const [familySearchOpen, setFamilySearchOpen] = React.useState(false)
    const [schoolFamilies, setSchoolFamilies] = React.useState([])
    const [discardUnsavedChangesPopup, setDiscardUnsavedChangesPopup] = React.useState(false)
    const [discardType, setDiscardType] = React.useState('')
    const [eventSaving, setEventSaving] = React.useState(false)
    const [eventSaveError, setEventSaveError] = React.useState('')
    const [hoveredCalendarSlot, setHoveredCalendarSlot] = React.useState(null)
    const [websiteCopyStatus, setWebsiteCopyStatus] = React.useState('idle')
    const syncCacheRef = React.useRef(new Map())
    const driveTimeRequestsRef = React.useRef(new Map())
    const websiteCopyResetRef = React.useRef(null)
    const mountedRef = React.useRef(true)

    React.useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            window.clearTimeout(websiteCopyResetRef.current)
        }
    }, [])

    React.useEffect(() => {
        if (!selectedTeacherKey) return
        if (calendarTeachers.some((teacher) => teacher.key === selectedTeacherKey)) return
        setSelectedTeacherKey('')
    }, [calendarTeachers, selectedTeacherKey])

    React.useEffect(() => {
        if (!preferredTeacherKey) {
            setSelectedTeacherKey('')
            return
        }
        if (!calendarTeachers.some((teacher) => teacher.key === preferredTeacherKey)) return
        setSelectedTeacherKey(preferredTeacherKey)
    }, [calendarTeachers, preferredTeacherKey])

    const selectedTeacherSummary = calendarTeachers.find(
        (teacher) => teacher.key === selectedTeacherKey
    ) || null
    const selectedTeacher = (Array.isArray(teachers) ? teachers : []).find(
        (teacher) => normalizeId(teacher?.TeacherID) === selectedTeacherSummary?.teacherId
    ) || getSuggestionObject(selectedTeacherSummary?.suggestion)
    const selectedTeacherId = selectedTeacherSummary?.teacherId || ''
    const selectedTeacherProfileUrl = getTeacherProfileUrl({
        ...selectedTeacher,
        Name: selectedTeacher?.Name
            || selectedTeacher?.teacherName
            || selectedTeacherSummary?.teacherName,
        School: selectedTeacher?.School || selectedTeacher?.school || schoolId,
    })
    const selectedTeacherColor = selectedTeacherSummary
        ? teacherColorMap[selectedTeacherSummary.key] || selectedTeacherSummary.color
        : '#64748B'
    const selectedTeacherAvailability = React.useMemo(
        () => getTeacherAvailabilityForSuggestion(
            selectedTeacherSummary?.suggestion,
            teachers
        ),
        [selectedTeacherSummary, teachers]
    )
    const selectedTeacherHomeDriveTime = React.useMemo(() => {
        if (!selectedTeacherKey) return ''
        const homeSuggestion = (Array.isArray(suggestions) ? suggestions : []).find(
            (suggestion) => {
                if (getSuggestionTeacherKey(suggestion) !== selectedTeacherKey) return false
                const suggestionObject = getSuggestionObject(suggestion)
                return suggestionObject?.type !== 'calendarEvent'
                    && !suggestionObject?.familyId
                    && !suggestionObject?.familyName
            }
        )
        if (!homeSuggestion) return ''
        const duration = String(homeSuggestion?.duration || '').trim()
        if (duration) return duration
        const minutes = Number(homeSuggestion?.sortingDuration)
        return Number.isFinite(minutes) ? `${Math.round(minutes)} min` : ''
    }, [selectedTeacherKey, suggestions])
    const weekDays = React.useMemo(
        () => Array.from({ length: 7 }, (_, index) => addDays(weekAnchor, index)),
        [weekAnchor]
    )
    const safeUserData = React.useMemo(
        () => ({ ...userData, Name: String(userData?.Name || 'Admin') }),
        [userData]
    )
    const schoolCalendar = React.useMemo(() => {
        const schoolAbbreviation = String(schoolId || '').trim().toLowerCase()
        if (!schoolAbbreviation) return null
        try {
            return {
                schoolAbbreviation,
                timeZone: getSchoolIANATimezone(schoolAbbreviation),
            }
        } catch (error) {
            console.error('Unable to determine the scheduler school timezone:', error)
            return null
        }
    }, [schoolId])
    const editorTeachers = React.useMemo(
        () => calendarTeachers.map((teacherSummary) => {
            const teacherProfile = (Array.isArray(teachers) ? teachers : []).find(
                (teacher) => normalizeId(teacher?.TeacherID) === teacherSummary.teacherId
            )
            if (teacherProfile) return teacherProfile
            const suggestionObject = getSuggestionObject(teacherSummary.suggestion)
            return {
                ...suggestionObject,
                TeacherID: teacherSummary.teacherId,
                Name: teacherSummary.teacherName,
                ImageName: suggestionObject.ImageName || suggestionObject.imageUrl || '',
            }
        }).sort((left, right) => String(left?.Name || '').localeCompare(String(right?.Name || ''))),
        [calendarTeachers, teachers]
    )

    const schedulerFamily = React.useMemo(() => {
        const students = Array.isArray(familyProfile?.students)
            ? familyProfile.students
            : []
        return {
            ...familyProfile,
            familyId: normalizeId(familyProfile?.familyId || familyId),
            familyName: String(familyProfile?.familyName || familyName || '').trim(),
            address: normalizeFamilyAddressRecord(familyProfile, familyAddress),
            students,
        }
    }, [familyAddress, familyId, familyName, familyProfile])

    const familyEventFields = React.useMemo(() => {
        const students = schedulerFamily.students
        const selectedStudent = students.length === 1 ? students[0] : null
        const selectedStudentName = getStudentName(selectedStudent)
        const selectedFamilyName = schedulerFamily.familyName
        return {
            name: selectedStudent
                ? `${selectedStudentName} ${selectedFamilyName}`.trim()
                : `${selectedFamilyName} Family`.trim(),
            familyId: schedulerFamily.familyId,
            familyName: selectedFamilyName,
            parentName: schedulerFamily.primaryParentName || schedulerFamily.parentName || '',
            familyPhoneNumber: schedulerFamily.primaryCellphone
                || schedulerFamily.familyPhoneNumber
                || '',
            familyAddress: schedulerFamily.address,
            familyEmail: schedulerFamily.primaryEmail || schedulerFamily.familyEmail || '',
            waveCustomerId: schedulerFamily.waveCustomerId || '',
            studentId: selectedStudent ? getStudentId(selectedStudent) : '',
            studentName: selectedStudentName,
            studentDOB: selectedStudent?.dob
                || selectedStudent?.DOB
                || selectedStudent?.dateOfBirth
                || '',
        }
    }, [schedulerFamily])

    const openNewCalendarEventAt = React.useCallback((date, minutes, prefillFamily = false) => {
        if (!selectedTeacherId || !schoolCalendar || !(date instanceof Date)) return
        const hours = Math.floor(minutes / 60)
        const minuteValue = minutes % 60
        const eventDate = new Date(date)
        eventDate.setHours(hours, minuteValue, 0, 0)
        const dateTime = `${format(date, 'yyyy-MM-dd')}T${String(hours).padStart(2, '0')}:${String(minuteValue).padStart(2, '0')}:00`
        const event = generateNewCalendarEvent(
            eventDate,
            schoolCalendar.schoolAbbreviation,
            safeUserData.Name,
            schoolCalendar.timeZone
        )
        setNewEvent({
            ...event,
            ...(prefillFamily ? familyEventFields : {}),
            isIntro: true,
            dateTime,
            teacherId: selectedTeacherId,
            teacherName: selectedTeacherSummary?.teacherName || '',
            teacherImageUrl: resolveTeacherImage(
                selectedTeacher,
                getSuggestionObject(selectedTeacherSummary?.suggestion)
            ),
        })
        setEventSaveError('')
        setOpenEvent(true)
    }, [familyEventFields, safeUserData.Name, schoolCalendar, selectedTeacher, selectedTeacherId, selectedTeacherSummary])

    const openNewCalendarEvent = React.useCallback(() => {
        if (!selectedTeacherId || !schoolCalendar) return
        const defaultDate = addDays(weekAnchor, 1)
        openNewCalendarEventAt(defaultDate, 18 * 60, false)
    }, [openNewCalendarEventAt, schoolCalendar, selectedTeacherId, weekAnchor])

    const copySelectedTeacherWebsite = React.useCallback(async () => {
        if (!selectedTeacherProfileUrl) return

        window.clearTimeout(websiteCopyResetRef.current)
        try {
            await navigator.clipboard.writeText(selectedTeacherProfileUrl)
            setWebsiteCopyStatus('copied')
        } catch (error) {
            console.error('Unable to copy the teacher website link:', error)
            setWebsiteCopyStatus('error')
        }
        websiteCopyResetRef.current = window.setTimeout(
            () => setWebsiteCopyStatus('idle'),
            2000
        )
    }, [selectedTeacherProfileUrl])

    React.useEffect(() => {
        window.clearTimeout(websiteCopyResetRef.current)
        setWebsiteCopyStatus('idle')
    }, [selectedTeacherKey])

    const getPointerMinutes = React.useCallback((mouseEvent) => {
        const bounds = mouseEvent.currentTarget.getBoundingClientRect()
        return getCalendarMinutesFromPointer(
            mouseEvent.clientY - bounds.top,
            bounds.height
        )
    }, [])

    const handleCalendarSlotHover = React.useCallback((mouseEvent, date) => {
        setHoveredCalendarSlot({
            dateKey: format(date, 'yyyy-MM-dd'),
            dayName: format(date, 'EEEE'),
            minutes: getPointerMinutes(mouseEvent),
        })
    }, [getPointerMinutes])

    const handleCalendarSlotClick = React.useCallback((mouseEvent, date) => {
        if (mouseEvent.target.closest(
            '.magic-scheduler-calendar__event, .magic-scheduler-calendar__ghost-event'
        )) return
        openNewCalendarEventAt(date, getPointerMinutes(mouseEvent), true)
    }, [getPointerMinutes, openNewCalendarEventAt])

    const createCalendarEvent = React.useCallback(async (localEvent) => {
        if (eventSaving || !schoolCalendar || !localEvent) return
        setEventSaving(true)
        setEventSaveError('')
        try {
            let latestCalendarEvents = calendarEvents
            const teacherId = normalizeId(localEvent.teacherId || selectedTeacherId)
            if (teacherId) {
                try {
                    const latestResponse = await axios.get(
                        `${baseUrl}/calendar/teacher/${teacherId}`,
                        getRequestHeaders()
                    )
                    if (Array.isArray(latestResponse?.data)) {
                        latestCalendarEvents = latestResponse.data
                        setCalendarEvents(latestCalendarEvents)
                    }
                } catch (refreshError) {
                    console.error(
                        'Unable to refresh the teacher calendar before saving:',
                        refreshError
                    )
                }
            }

            const recurringEventsInSchoolTime = latestCalendarEvents.map((calendarEvent) => {
                try {
                    return prepareSchedulerRecordForSchoolTime(
                        calendarEvent,
                        schoolId,
                        true
                    )
                } catch (error) {
                    return calendarEvent
                }
            })
            const cancellationsInSchoolTime = calendarCancellations.map((cancellation) => {
                try {
                    return prepareSchedulerRecordForSchoolTime(cancellation, schoolId)
                } catch (error) {
                    return cancellation
                }
            })
            const reservedConflict = getRecurringExceptionConflictForEvent(
                localEvent,
                recurringEventsInSchoolTime,
                cancellationsInSchoolTime
            )
            if (reservedConflict) {
                const reservedName = reservedConflict.studentName
                    || reservedConflict.familyName
                    || 'The regular student'
                setEventSaveError(reservedConflict.exceptionType === 'cancellation'
                    ? `${reservedName} cancelled this occurrence, but the recurring lesson returns. Choose another time.`
                    : `${reservedName} has a regular recurring lesson at this time. This occurrence was moved or skipped, so please choose another time.`)
                return
            }

            const event = {
                ...localEvent,
                teacherName: String(localEvent.teacherName || '').trim(),
                dateTime: convertStringToUTCString(
                    localEvent.dateTime,
                    schoolCalendar.timeZone
                ),
            }
            if (findDuplicateCalendarEvent(event, latestCalendarEvents)) {
                setEventSaveError(
                    "This exact lesson is already on the teacher's calendar. No duplicate was created."
                )
                return
            }
            const response = await axios.post(
                `${baseUrl}/calendar`,
                { event },
                getRequestHeaders()
            )
            const savedEvent = response?.data || event
            const savedTeacherId = normalizeId(
                savedEvent.teacherId
                || savedEvent.teacherID
                || savedEvent.TeacherID
                || localEvent.teacherId
            )
            const savedTeacherSummary = calendarTeachers.find(
                (teacher) => teacher.teacherId === savedTeacherId
            )
            if (savedTeacherSummary) setSelectedTeacherKey(savedTeacherSummary.key)
            setCalendarEvents((current) => [
                ...current.filter((existing) => (
                    normalizeId(existing.id || existing._id)
                    !== normalizeId(savedEvent.id || savedEvent._id)
                )),
                savedEvent,
            ])
            if (socket?.emit) {
                socket.emit('createCalendarEvent', {
                    emitter: safeUserData.Name,
                    packet: savedEvent,
                })
            }
            setOpenEvent(false)
            setNewEvent(false)
        } catch (error) {
            console.error('Unable to create the calendar event:', error)
            setEventSaveError(
                error?.response?.status === 409
                    ? error.response?.data?.message
                        || "This exact lesson is already on the teacher's calendar."
                    : 'The event could not be saved. Please check the details and try again.'
            )
        } finally {
            setEventSaving(false)
        }
    }, [
        calendarCancellations,
        calendarEvents,
        calendarTeachers,
        eventSaving,
        safeUserData.Name,
        schoolCalendar,
        schoolId,
        selectedTeacherId,
        socket,
    ])

    const loadDriveTime = React.useCallback((event, routeKey) => {
        if (driveTimeRequestsRef.current.has(routeKey)) {
            return driveTimeRequestsRef.current.get(routeKey)
        }

        const eventAddress = formatMapAddress(event?.familyAddress || event?.address)
        const eventIdentity = getEventRouteIdentity(event)
        const targetIdentity = String(familyId || familyAddress || '').trim()
        if (!schoolId || !eventAddress || !familyAddress || !eventIdentity || !targetIdentity) {
            return Promise.resolve(null)
        }

        const eventFamilyId = event?.familyId || event?.familyID || event?.FamilyID
        const fromKey = getRouteEndpointKey(eventFamilyId, eventIdentity)
        const toKey = getRouteEndpointKey(familyId, targetIdentity)
        if (fromKey === toKey) return Promise.resolve('0 min')

        const edgeParams = {
            schoolId,
            fromKey,
            toKey,
            bucket: getEventDriveBucket(event),
        }
        const getEdge = () => axios.get(
            `${baseUrl}/drive-edge`,
            { ...getRequestHeaders(), params: edgeParams }
        )
        const readMinutes = (response) => {
            const rawMinutes = response?.data?.minutes
            if (rawMinutes === null || rawMinutes === undefined || rawMinutes === '') {
                return null
            }
            const minutes = Number(rawMinutes)
            return Number.isFinite(minutes) && minutes >= 0 ? `${minutes} min` : null
        }

        const request = (async () => {
            const cachedEdge = await getEdge()
            const cachedDriveTime = readMinutes(cachedEdge)
            if (cachedDriveTime) return cachedDriveTime

            await axios.post(
                `${baseUrl}/place-directories/bulk`,
                {
                    schoolId,
                    directories: [
                        {
                            key: fromKey,
                            address: eventAddress,
                            roles: ['student-home'],
                            familyIds: eventFamilyId ? [String(eventFamilyId)] : [],
                        },
                        {
                            key: toKey,
                            address: familyAddress,
                            roles: ['target-family'],
                            familyIds: familyId ? [String(familyId)] : [],
                        },
                    ],
                },
                getRequestHeaders()
            )
            return readMinutes(await getEdge())
        })().catch((error) => {
            console.error('Unable to load the calendar event drive time:', error)
            return null
        }).finally(() => {
            driveTimeRequestsRef.current.delete(routeKey)
        })

        driveTimeRequestsRef.current.set(routeKey, request)
        return request
    }, [familyAddress, familyId, schoolId])

    const handleEventHover = React.useCallback((mouseEvent, event, eventName, suggestionDriveTime) => {
        const bounds = mouseEvent.currentTarget.getBoundingClientRect()
        const eventStart = new Date(event?.dateTime)
        const eventDuration = Math.max(15, Number(event?.lessonLength) || 30)
        const eventEnd = new Date(eventStart.getTime() + (eventDuration * 60 * 1000))
        const timeRange = Number.isNaN(eventStart.getTime())
            ? ''
            : `${format(eventStart, 'h:mm a')} – ${format(eventEnd, 'h:mm a')}`
        const routeIdentity = getEventRouteIdentity(event)
        const routeKey = getEventDriveTimeRouteKey(
            event,
            schoolId,
            familyId,
            familyAddress
        )
        const hasResolvedDriveTime = Object.prototype.hasOwnProperty.call(
            resolvedDriveTimes,
            routeKey
        )
        const storedDriveTime = hasResolvedDriveTime
            ? resolvedDriveTimes[routeKey]
            : null
        const driveTime = suggestionDriveTime || storedDriveTime
        const canLoad = Boolean(
            !driveTime
            && !hasResolvedDriveTime
            && routeIdentity
            && schoolId
            && familyAddress
            && formatMapAddress(event?.familyAddress || event?.address)
        )

        setHoveredEvent({
            driveTime,
            eventName,
            left: bounds.left + (bounds.width / 2),
            loading: canLoad,
            routeKey,
            timeRange,
            top: bounds.top - 8,
        })

        if (!canLoad) return
        loadDriveTime(event, routeKey).then((loadedDriveTime) => {
            if (!mountedRef.current) return
            setResolvedDriveTimes((current) => ({
                ...current,
                [routeKey]: loadedDriveTime,
            }))
            setHoveredEvent((current) => (
                current?.routeKey === routeKey
                    ? { ...current, driveTime: loadedDriveTime, loading: false }
                    : current
            ))
        })
    }, [familyAddress, familyId, loadDriveTime, resolvedDriveTimes, schoolId])

    React.useEffect(() => {
        if (!selectedTeacherId) {
            setCalendarEvents([])
            setCalendarCancellations([])
            setCalendarError('')
            setCancellationLoadWarning('')
            return undefined
        }
        let active = true
        setCalendarLoading(true)
        setCalendarError('')
        setCancellationLoadWarning('')
        Promise.all([
            axios.get(
                `${baseUrl}/calendar/teacher/${selectedTeacherId}`,
                getRequestHeaders()
            ),
            axios.get(
                `${baseUrl}/cancellation/teacher/${selectedTeacherId}`,
                getRequestHeaders()
            ).then((response) => ({ response })).catch((error) => ({ error })),
        ]).then(([calendarResponse, cancellationResult]) => {
            if (!active) return
            setCalendarEvents(
                Array.isArray(calendarResponse?.data) ? calendarResponse.data : []
            )
            if (cancellationResult.error) {
                console.error(
                    'Unable to load cancelled recurring lesson times:',
                    cancellationResult.error
                )
                setCalendarCancellations([])
                setCancellationLoadWarning(
                    'Cancellation details could not be checked. Recurring exception times are still reserved.'
                )
            } else {
                setCalendarCancellations(
                    Array.isArray(cancellationResult.response?.data)
                        ? cancellationResult.response.data
                        : []
                )
            }
        }).catch((error) => {
            console.error('Unable to load the Magic Scheduler teacher calendar:', error)
            if (active) {
                setCalendarEvents([])
                setCalendarCancellations([])
                setCalendarError('This teacher’s lesson calendar is unavailable right now.')
            }
        }).finally(() => {
            if (active) setCalendarLoading(false)
        })
        return () => {
            active = false
        }
    }, [selectedTeacherId])

    const calendarEventsByDay = React.useMemo(() => {
        if (!calendarEvents.length) return {}
        let organized = {}
        createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
            weekDays,
            calendarEvents.filter((event) => !event?.isPendingOffer),
            (result) => {
                organized = result || {}
            }
        )
        return organized
    }, [calendarEvents, weekDays])

    const schoolTimeCalendarEvents = React.useMemo(
        () => calendarEvents.map((event) => {
            try {
                return prepareSchedulerRecordForSchoolTime(event, schoolId, true)
            } catch (error) {
                console.error('Unable to prepare a recurring calendar event:', error)
                return event
            }
        }),
        [calendarEvents, schoolId]
    )

    const schoolTimeCalendarCancellations = React.useMemo(
        () => calendarCancellations.map((cancellation) => {
            try {
                return prepareSchedulerRecordForSchoolTime(cancellation, schoolId)
            } catch (error) {
                console.error('Unable to prepare a cancelled lesson time:', error)
                return cancellation
            }
        }),
        [calendarCancellations, schoolId]
    )

    React.useEffect(() => {
        if (!selectedTeacherId || !schoolId || !familyAddress) return

        schoolTimeCalendarEvents.forEach((event) => {
            const suggestionDriveTime = getCalendarEventDriveTime(
                event,
                suggestions,
                selectedTeacherId
            )
            if (suggestionDriveTime) return

            const routeKey = getEventDriveTimeRouteKey(
                event,
                schoolId,
                familyId,
                familyAddress
            )
            if (
                !routeKey
                || Object.prototype.hasOwnProperty.call(resolvedDriveTimes, routeKey)
            ) return

            loadDriveTime(event, routeKey).then((driveTime) => {
                if (!mountedRef.current) return
                setResolvedDriveTimes((current) => (
                    Object.prototype.hasOwnProperty.call(current, routeKey)
                        ? current
                        : { ...current, [routeKey]: driveTime }
                ))
            })
        })
    }, [
        familyAddress,
        familyId,
        loadDriveTime,
        resolvedDriveTimes,
        schoolId,
        schoolTimeCalendarEvents,
        selectedTeacherId,
        suggestions,
    ])

    const syncKey = `${selectedTeacherId}:${format(weekAnchor, 'yyyy-MM-dd')}`
    React.useEffect(() => {
        if (!selectedTeacherId) {
            setSyncStatus('idle')
            return undefined
        }
        const cachedStatus = syncCacheRef.current.get(syncKey)
        if (cachedStatus) {
            setSyncStatus(cachedStatus)
            return undefined
        }

        let active = true
        setSyncStatus('loading')
        const teacherRecordId = getTeacherRecordId(selectedTeacher)
        const teacherIdentifier = teacherRecordId || selectedTeacherId
        const numericTeacherId = Number(teacherIdentifier)
        const rangeStart = new Date(weekAnchor)
        rangeStart.setHours(8, 0, 0, 0)
        const rangeEnd = addDays(rangeStart, 7)
        const payload = {
            teacherId: Number.isFinite(numericTeacherId) ? numericTeacherId : teacherIdentifier,
            teacherLegacyId: selectedTeacherId,
            start: rangeStart.toISOString(),
            end: rangeEnd.toISOString(),
            durationMinutes: 30,
            intervalMinutes: 15,
        }
        if (teacherRecordId && teacherRecordId !== selectedTeacherId) {
            payload.teacherRecordId = teacherRecordId
        }
        const teacherEmail = getTeacherEmail(selectedTeacher)
        if (teacherEmail) payload.email = teacherEmail

        axios.post(
            `${baseUrl}/api/integrations/nylas/availability`,
            payload,
            getRequestHeaders()
        ).then(() => {
            if (!active) return
            syncCacheRef.current.set(syncKey, 'synced')
            setSyncStatus('synced')
        }).catch((error) => {
            if (!active) return
            const status = error?.response?.status === 404 ? 'not-connected' : 'error'
            syncCacheRef.current.set(syncKey, status)
            setSyncStatus(status)
        })
        return () => {
            active = false
        }
    }, [selectedTeacher, selectedTeacherId, syncKey, weekAnchor])

    const weekEnd = endOfWeek(weekAnchor, { weekStartsOn: 0 })
    const weekTitle = isSameDay(weekAnchor, todayWeekStart)
        ? 'This Week'
        : isSameDay(weekAnchor, nextWeekStart)
            ? 'Next Week'
            : `Week of ${format(weekAnchor, 'MMM d')}`
    const weekLabel = `${format(weekAnchor, 'MMM d')} – ${format(weekEnd, 'MMM d')}`

    return (
        <div className={`magic-scheduler-calendar${showTeacherList ? '' : ' without-teacher-list'}`}>
            {showTeacherList && (
            <aside className='magic-scheduler-calendar__teachers'>
                <button
                    type='button'
                    className={`magic-scheduler__availability-match${availabilityMatchOn ? ' is-on' : ' is-off'}`}
                    role='switch'
                    aria-checked={availabilityMatchOn}
                    aria-label='Availability Match'
                    onClick={onToggleAvailabilityMatch}
                >
                    <span className='magic-scheduler__availability-match-track'><span /></span>
                    Availability Match
                </button>
                <div className='magic-scheduler-calendar__teacher-list'>
                    {calendarTeachers.map((teacherSummary) => {
                        const suggestionObject = getSuggestionObject(teacherSummary.suggestion)
                        const teacher = (Array.isArray(teachers) ? teachers : []).find(
                            (candidate) => normalizeId(candidate?.TeacherID) === teacherSummary.teacherId
                        )
                        const imageUrl = resolveTeacherImage(teacher, suggestionObject)
                        const instruments = getInstrumentLabels(teacher, suggestionObject)
                        const selected = teacherSummary.key === selectedTeacherKey
                        return (
                            <button
                                type='button'
                                key={teacherSummary.key}
                                className={`magic-scheduler-calendar__teacher${selected ? ' is-selected' : ''}`}
                                style={{
                                    '--teacher-color': teacherColorMap[teacherSummary.key]
                                        || teacherSummary.color,
                                }}
                                onClick={() => setSelectedTeacherKey(teacherSummary.key)}
                                aria-pressed={selected}
                                aria-label={`View ${teacherSummary.teacherName} calendar`}
                            >
                                {imageUrl ? (
                                    <img src={imageUrl} alt='' />
                                ) : (
                                    <span className='magic-scheduler-calendar__teacher-avatar'>
                                        {teacherSummary.teacherName.charAt(0)}
                                    </span>
                                )}
                                <span className='magic-scheduler-calendar__teacher-copy'>
                                    <strong>{teacherSummary.teacherName}</strong>
                                    {instruments.length > 0 && (
                                        <span className='magic-scheduler-calendar__teacher-instruments'>
                                            {instruments.map((instrument) => (
                                                <span key={instrument}>{instrument}</span>
                                            ))}
                                        </span>
                                    )}
                                </span>
                            </button>
                        )
                    })}
                    {calendarTeachers.length === 0 && (
                        <div className='magic-scheduler-calendar__empty-teachers'>
                            {availabilityMatchOn
                                ? 'No instrument-matched teachers overlap this family’s availability.'
                                : 'No instrument-matched teachers were found.'}
                        </div>
                    )}
                </div>
            </aside>
            )}

            <section className='magic-scheduler-calendar__panel'>
                <div className='magic-scheduler-calendar__toolbar'>
                    <div className={`magic-scheduler-calendar__sync is-${syncStatus}`}>
                        <span />
                        {syncStatus === 'loading' && 'Checking Google Calendar…'}
                        {syncStatus === 'synced' && 'Google Calendar is Synced.'}
                        {syncStatus === 'not-connected' && 'No connected personal calendar.'}
                        {syncStatus === 'error' && 'Google Calendar is unavailable.'}
                        {syncStatus === 'idle' && 'Select a teacher'}
                    </div>
                    <div className='magic-scheduler-calendar__week-navigation'>
                        <button
                            type='button'
                            className='magic-scheduler-calendar__today-button'
                            onClick={() => setWeekAnchor(
                                startOfWeek(new Date(), { weekStartsOn: 0 })
                            )}
                        >
                            Today
                        </button>
                        <button
                            type='button'
                            aria-label='Previous week'
                            onClick={() => setWeekAnchor((current) => subWeeks(current, 1))}
                        >
                            <MdChevronLeft size={22} />
                        </button>
                        <div>
                            <strong>{weekTitle}</strong>
                            <span>{weekLabel}</span>
                        </div>
                        <button
                            type='button'
                            aria-label='Next week'
                            onClick={() => setWeekAnchor((current) => addWeeks(current, 1))}
                        >
                            <MdChevronRight size={22} />
                        </button>
                    </div>
                    <div className='magic-scheduler-calendar__actions'>
                        <button
                            type='button'
                            className={`magic-scheduler-calendar__copy-website is-${websiteCopyStatus}`}
                            disabled={!selectedTeacherId || !selectedTeacherProfileUrl}
                            title={selectedTeacherId && !selectedTeacherProfileUrl
                                ? 'This teacher does not have a website profile link.'
                                : ''}
                            onClick={copySelectedTeacherWebsite}
                        >
                            <MdContentCopy size={16} aria-hidden='true' />
                            {websiteCopyStatus === 'copied' ? 'Copied!' : 'Copy Website'}
                        </button>
                        <button
                            type='button'
                            className='magic-scheduler-calendar__add-event'
                            disabled={!selectedTeacherId || !schoolCalendar || eventSaving}
                            onClick={openNewCalendarEvent}
                        >
                            <MdAdd size={20} />
                            {eventSaving ? 'Saving…' : 'Add Event'}
                        </button>
                    </div>
                </div>

                {eventSaveError && (
                    <div className='magic-scheduler-calendar__event-save-error'>
                        {eventSaveError}
                    </div>
                )}
                {calendarError && (
                    <div className='magic-scheduler-calendar__error'>{calendarError}</div>
                )}
                {cancellationLoadWarning && (
                    <div className='magic-scheduler-calendar__cancellation-warning'>
                        {cancellationLoadWarning}
                    </div>
                )}
                <div className='magic-scheduler-calendar__calendar'>
                    {calendarLoading && (
                        <div className='magic-scheduler-calendar__loading'>
                            Loading {selectedTeacherSummary?.teacherName || 'teacher'}’s calendar…
                        </div>
                    )}
                    <div className='magic-scheduler-calendar__time-scale'>
                        {HOURS.map((minutes) => (
                            <span key={minutes}>{formatMinutes(minutes).replace(':00', '')}</span>
                        ))}
                    </div>
                    <div className='magic-scheduler-calendar__week-grid'>
                        {weekDays.map((date) => {
                            const dateKey = format(date, 'yyyy-MM-dd')
                            const isToday = isSameDay(date, new Date())
                            const availabilityBlocks = getFamilyAvailabilityBlocksForDate(
                                familyAvailability,
                                date
                            )
                            const teacherAvailabilityBlocks = getTeacherAvailabilityBlocksForDate(
                                selectedTeacherAvailability,
                                date
                            )
                            const events = calendarEventsByDay?.[dateKey]?.events || []
                            const everyOtherWeekGhosts = getEveryOtherWeekGhostEventsForDate(
                                schoolTimeCalendarEvents,
                                date
                            )
                            const recurringExceptionGhosts = getRecurringExceptionGhostEventsForDate(
                                schoolTimeCalendarEvents,
                                schoolTimeCalendarCancellations,
                                date
                            )
                            return (
                                <div
                                    className={`magic-scheduler-calendar__day${isToday ? ' is-today' : ''}`}
                                    key={dateKey}
                                >
                                    <div
                                        className={`magic-scheduler-calendar__day-header${isToday ? ' is-today' : ''}`}
                                        aria-current={isToday ? 'date' : undefined}
                                    >
                                        <strong>{format(date, 'EEE')}</strong>
                                        <span>{format(date, 'MMM d')}</span>
                                    </div>
                                    <div
                                        className='magic-scheduler-calendar__day-grid'
                                        aria-label={`${format(date, 'EEEE MMM d')} calendar`}
                                        data-date={dateKey}
                                        onMouseMove={(mouseEvent) => handleCalendarSlotHover(
                                            mouseEvent,
                                            date
                                        )}
                                        onMouseLeave={() => setHoveredCalendarSlot((current) => (
                                            current?.dateKey === dateKey ? null : current
                                        ))}
                                        onClick={(mouseEvent) => handleCalendarSlotClick(
                                            mouseEvent,
                                            date
                                        )}
                                    >
                                        {HOURS.map((minutes) => (
                                            <span
                                                className='magic-scheduler-calendar__hour-line'
                                                key={minutes}
                                                style={{ top: `${getCalendarPosition(minutes)}%` }}
                                            />
                                        ))}
                                        {hoveredCalendarSlot?.dateKey === dateKey && (
                                            <div
                                                className={`magic-scheduler-calendar__hover-time${hoveredCalendarSlot.minutes <= CALENDAR_START_MINUTES + 15 ? ' is-near-start' : ''}${hoveredCalendarSlot.minutes >= CALENDAR_END_MINUTES - 30 ? ' is-near-end' : ''}`}
                                                aria-label={`${hoveredCalendarSlot.dayName} ${formatMinutes(hoveredCalendarSlot.minutes)}`}
                                                style={{
                                                    top: `${getCalendarPosition(hoveredCalendarSlot.minutes)}%`,
                                                }}
                                            >
                                                <span>{formatMinutes(hoveredCalendarSlot.minutes)}</span>
                                            </div>
                                        )}
                                        {availabilityBlocks.map((block) => {
                                            const top = getCalendarPosition(block.start)
                                            const bottom = getCalendarPosition(block.end)
                                            const dayName = format(date, 'EEEE')
                                            const displayedTimeRange = formatAvailabilityTimeRange(
                                                block.start,
                                                block.end
                                            )
                                            const label = `${dayName} family availability ${formatMinutes(block.start)} – ${formatMinutes(block.end)}`
                                            return (
                                                <div
                                                    className='magic-scheduler-calendar__family-availability'
                                                    key={`${block.start}-${block.end}`}
                                                    aria-label={label}
                                                    title={label}
                                                    style={{
                                                        top: `${top}%`,
                                                        height: `${bottom - top}%`,
                                                    }}
                                                >
                                                    <span className='magic-scheduler-calendar__family-availability-time'>
                                                        {displayedTimeRange}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                        {teacherAvailabilityBlocks.map((block) => {
                                            const top = getCalendarPosition(block.start)
                                            const bottom = getCalendarPosition(block.end)
                                            const dayName = format(date, 'EEEE')
                                            const teacherName = selectedTeacherSummary?.teacherName || 'Teacher'
                                            const displayedTimeRange = formatAvailabilityTimeRange(
                                                block.start,
                                                block.end
                                            )
                                            const label = `${teacherName} availability on ${dayName}, ${formatMinutes(block.start)} – ${formatMinutes(block.end)}`
                                            return (
                                                <div
                                                    className='magic-scheduler-calendar__teacher-availability'
                                                    key={`teacher-${block.start}-${block.end}`}
                                                    aria-label={label}
                                                    title={label}
                                                    style={{
                                                        '--teacher-color': selectedTeacherColor,
                                                        top: `${top}%`,
                                                        height: `${bottom - top}%`,
                                                    }}
                                                >
                                                    {selectedTeacherHomeDriveTime && (
                                                        <span
                                                            className='magic-scheduler-calendar__teacher-home-drive'
                                                            aria-label={`${selectedTeacherHomeDriveTime} drive from ${teacherName} home`}
                                                        >
                                                            <MdHome size={11} aria-hidden='true' />
                                                            {selectedTeacherHomeDriveTime}
                                                        </span>
                                                    )}
                                                    <span className='magic-scheduler-calendar__teacher-availability-time'>
                                                        {displayedTimeRange}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                        {events.map((event, index) => {
                                            const startDate = new Date(event.dateTime)
                                            if (Number.isNaN(startDate.getTime())) return null
                                            const startMinutes = (startDate.getHours() * 60) + startDate.getMinutes()
                                            const duration = Math.max(15, Number(event.lessonLength) || 30)
                                            const endMinutes = startMinutes + duration
                                            if (
                                                endMinutes <= CALENDAR_START_MINUTES
                                                || startMinutes >= CALENDAR_END_MINUTES
                                            ) return null
                                            const top = getCalendarPosition(startMinutes)
                                            const bottom = getCalendarPosition(endMinutes)
                                            const eventName = event.studentName || event.familyName || 'Lesson'
                                            const driveTime = getCalendarEventDriveTime(
                                                event,
                                                suggestions,
                                                selectedTeacherId
                                            )
                                            const routeKey = getEventDriveTimeRouteKey(
                                                event,
                                                schoolId,
                                                familyId,
                                                familyAddress
                                            )
                                            const hasResolvedDriveTime = Object.prototype.hasOwnProperty.call(
                                                resolvedDriveTimes,
                                                routeKey
                                            )
                                            const displayedDriveTime = driveTime
                                                || (hasResolvedDriveTime
                                                    ? resolvedDriveTimes[routeKey]
                                                    : null)
                                            const isHighlighted = calendarEventMatchesSuggestion(
                                                event,
                                                highlightedSuggestion,
                                                selectedTeacherId
                                            )
                                            return (
                                                <div
                                                    className={`magic-scheduler-calendar__event${event.isIntro ? ' is-intro' : ''}${isHighlighted ? ' is-highlighted-suggestion' : ''}`}
                                                    key={event.id || event._id || `${dateKey}-${index}`}
                                                    style={{
                                                        '--teacher-color': selectedTeacherColor,
                                                        ...(event.isIntro ? {
                                                            '--event-background-color': INTRO_EVENT_BACKGROUND_COLOR,
                                                            '--event-border-color': INTRO_EVENT_BORDER_COLOR,
                                                            '--event-text-color': INTRO_EVENT_TEXT_COLOR,
                                                        } : {}),
                                                        top: `${top}%`,
                                                        height: `${Math.max(3, bottom - top)}%`,
                                                    }}
                                                    title={`${eventName} · ${format(startDate, 'h:mm a')}`}
                                                    onMouseEnter={(mouseEvent) => handleEventHover(
                                                        mouseEvent,
                                                        event,
                                                        eventName,
                                                        driveTime
                                                    )}
                                                    onMouseLeave={() => setHoveredEvent(null)}
                                                >
                                                    <strong>{eventName}</strong>
                                                    <span className='magic-scheduler-calendar__event-meta'>
                                                        <span>{format(startDate, 'h:mm a')}</span>
                                                        <span className='magic-scheduler-calendar__event-drive-time'>
                                                            <MdDirectionsCar size={10} aria-hidden='true' />
                                                            {displayedDriveTime || (
                                                                hasResolvedDriveTime
                                                                    ? 'Unavailable'
                                                                    : 'Loading…'
                                                            )}
                                                        </span>
                                                    </span>
                                                </div>
                                            )
                                        })}
                                        {recurringExceptionGhosts.map((event, index) => {
                                            const startDate = new Date(event.dateTime)
                                            if (Number.isNaN(startDate.getTime())) return null
                                            const startMinutes = (startDate.getHours() * 60) + startDate.getMinutes()
                                            const duration = Math.max(15, Number(event.lessonLength) || 30)
                                            const endMinutes = startMinutes + duration
                                            if (
                                                endMinutes <= CALENDAR_START_MINUTES
                                                || startMinutes >= CALENDAR_END_MINUTES
                                            ) return null
                                            const top = getCalendarPosition(startMinutes)
                                            const bottom = getCalendarPosition(endMinutes)
                                            const eventName = event.studentName || event.familyName || 'Lesson'
                                            const cancelledBy = event.cancelledBy === 'teacher'
                                                ? 'Teacher cancelled'
                                                : 'Family cancelled'
                                            const statusText = event.exceptionType === 'cancellation'
                                                ? `${cancelledBy} · recurring time reserved`
                                                : event.exceptionType === 'moved'
                                                    ? 'Lesson moved · regular time reserved'
                                                    : 'Regular lesson returns · time reserved'
                                            const label = event.exceptionType === 'cancellation'
                                                ? `${eventName}: ${cancelledBy.toLowerCase()} this occurrence, but the recurring lesson returns and this time remains reserved`
                                                : event.exceptionType === 'moved'
                                                    ? `${eventName}: lesson moved for this occurrence, but the regular recurring time remains reserved`
                                                    : `${eventName}: this occurrence was skipped, but the regular recurring time remains reserved`
                                            return (
                                                <div
                                                    className={`magic-scheduler-calendar__ghost-event is-recurring-exception${event.exceptionType === 'cancellation' ? ' is-cancelled-occurrence' : ''}${event.exceptionType === 'moved' ? ' is-moved-occurrence' : ''}`}
                                                    key={`recurring-exception-${event.cancellationOccurrenceKey || event.cancellationId || index}-${dateKey}`}
                                                    aria-label={label}
                                                    style={{
                                                        '--teacher-color': selectedTeacherColor,
                                                        top: `${top}%`,
                                                        height: `${Math.max(3, bottom - top)}%`,
                                                    }}
                                                    title={label}
                                                >
                                                    <strong>{eventName}</strong>
                                                    <span>{statusText}</span>
                                                </div>
                                            )
                                        })}
                                        {everyOtherWeekGhosts.map((event, index) => {
                                            const startDate = new Date(event.dateTime)
                                            if (Number.isNaN(startDate.getTime())) return null
                                            const startMinutes = (startDate.getHours() * 60) + startDate.getMinutes()
                                            const duration = Math.max(15, Number(event.lessonLength) || 30)
                                            const endMinutes = startMinutes + duration
                                            if (
                                                endMinutes <= CALENDAR_START_MINUTES
                                                || startMinutes >= CALENDAR_END_MINUTES
                                            ) return null
                                            const top = getCalendarPosition(startMinutes)
                                            const bottom = getCalendarPosition(endMinutes)
                                            const eventName = event.studentName || event.familyName || 'Lesson'
                                            const label = `${eventName} has an every-other-week lesson at this time next week`
                                            return (
                                                <div
                                                    className='magic-scheduler-calendar__ghost-event'
                                                    key={`ghost-${event.id || event._id || index}-${dateKey}`}
                                                    aria-label={label}
                                                    style={{
                                                        '--teacher-color': selectedTeacherColor,
                                                        top: `${top}%`,
                                                        height: `${Math.max(3, bottom - top)}%`,
                                                    }}
                                                    title={label}
                                                >
                                                    <strong>{eventName}</strong>
                                                    <span>Reserved · lesson next week</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>
            {hoveredEvent && (
                <div
                    className='magic-scheduler-calendar__drive-tooltip'
                    role='tooltip'
                    style={{ left: hoveredEvent.left, top: hoveredEvent.top }}
                >
                    <div className='magic-scheduler-calendar__drive-tooltip-header'>
                        <span className='magic-scheduler-calendar__drive-tooltip-name'>
                            {hoveredEvent.eventName}
                        </span>
                        <strong className='magic-scheduler-calendar__drive-tooltip-duration'>
                            <MdDirectionsCar size={14} aria-hidden='true' />
                            {hoveredEvent.loading
                                ? 'Loading…'
                                : hoveredEvent.driveTime || 'Unavailable'}
                        </strong>
                    </div>
                    {hoveredEvent.timeRange && (
                        <span className='magic-scheduler-calendar__drive-tooltip-time'>
                            {hoveredEvent.timeRange}
                        </span>
                    )}
                </div>
            )}
            {schoolCalendar && newEvent && (
                <>
                    <NewCalendarEvent
                        setOpenEvent={setOpenEvent}
                        openEvent={openEvent}
                        newEvent={newEvent}
                        setNewEvent={setNewEvent}
                        teachers={editorTeachers}
                        setFamilySearchOpen={setFamilySearchOpen}
                        schoolCalendar={schoolCalendar}
                        setSchoolFamilies={setSchoolFamilies}
                        userData={safeUserData}
                        createCalendarEvent={createCalendarEvent}
                        saveError={eventSaveError}
                        setDiscardUnsavedChangesPopup={setDiscardUnsavedChangesPopup}
                        setDiscardType={setDiscardType}
                    />
                    <FamilySearchPopup
                        familySearchOpen={familySearchOpen}
                        setFamilySearchOpen={setFamilySearchOpen}
                        schoolFamilies={schoolFamilies}
                        setNewEvent={setNewEvent}
                        setNewOfferObject={() => {}}
                        setEventObjectToEdit={() => {}}
                        eventObjectToEdit={false}
                        openEvent={openEvent}
                        openOffer={false}
                        school={schoolCalendar.schoolAbbreviation}
                        preselectedFamily={
                            newEvent?.familyId === schedulerFamily.familyId
                                ? schedulerFamily
                                : null
                        }
                    />
                    <UnsavedCalendarEventPopup
                        open={discardUnsavedChangesPopup}
                        setOpen={setDiscardUnsavedChangesPopup}
                        discardType={discardType}
                        setNewEvent={setNewEvent}
                        setOpenEvent={setOpenEvent}
                        setEditEventToggle={() => {}}
                        setOpenOffer={() => {}}
                    />
                </>
            )}
        </div>
    )
}

export default MagicSchedulerCalendar
